// Portfolio Administration v1.0 PR 3 §7.2 + Loan record foundations — Setup
// tab. Leads with the progress header ("N of M complete · N blocked · N
// waived" + completion bar + the Mark-Setup-Complete gate state), then
// renders the LS checklist grouped into the seven workflow sections
// (catalog Section column; tier chips stay on each row). ADMIN can author
// the catalog from here (+ Add setup item / Edit catalog item) — changes
// propagate to every loan because checklist rows FK the catalog by GUID.

import { useMemo, useState } from "react";
import { Plus } from "lucide-react";
import { Button, Card } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { useAuth } from "@/hooks/useAuth";
import { ApiError } from "@/api/client";
import { SetupChecklistRow } from "../setup/SetupChecklistRow";
import { SetupChecklistRowModal } from "../setup/SetupChecklistRowModal";
import { SetupCatalogCreateModal } from "../setup/SetupCatalogCreateModal";
import { SetupCatalogEditModal } from "../setup/SetupCatalogEditModal";
import {
  useGenerateSetupChecklist,
  useLoanSetupCatalog,
  useLoanSetupChecklist,
  useMarkSetupComplete,
} from "@/hooks/portfolioAdmin/usePortfolioAdmin";
import {
  LOAN_SETUP_SECTIONS,
  type LoanSetupChecklistRowDto,
  type LoanSummaryDto,
} from "@/api/portfolioAdmin/types";

interface SetupTabProps {
  loan: LoanSummaryDto;
}

const OTHER_SECTION = "Other";
const DONE = new Set(["Complete", "Certified", "Waived"]);

function gateSatisfied(rows: LoanSetupChecklistRowDto[], tier: string): boolean {
  return rows
    .filter((r) => r.catalogTier === tier)
    .every((r) => DONE.has(r.status));
}

export function SetupTab({ loan }: SetupTabProps) {
  const { data, isLoading, isError } = useLoanSetupChecklist(loan.id);
  const [openRowId, setOpenRowId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editCatalogId, setEditCatalogId] = useState<string | null>(null);

  const toast = useToast();
  const auth = useAuth();
  const roleCodes = new Set(auth.user?.roleCodes ?? []);
  const permissions = new Set(auth.user?.permissions ?? []);
  const isAdmin = roleCodes.has("ADMIN");
  const canMarkComplete =
    roleCodes.has("B4") || roleCodes.has("E1") || roleCodes.has("ADMIN");
  const canGenerate = permissions.has("Loans.Manage") || isAdmin;

  const markComplete = useMarkSetupComplete(loan.id);
  const generate = useGenerateSetupChecklist(loan.id);
  // The catalog query only runs for admins (drives the edit modal).
  const catalog = useLoanSetupCatalog(isAdmin);

  const rows = useMemo(() => data ?? [], [data]);
  const sections = useMemo(() => {
    const order: string[] = [...LOAN_SETUP_SECTIONS, OTHER_SECTION];
    const grouped = new Map<string, LoanSetupChecklistRowDto[]>(
      order.map((s) => [s, []]),
    );
    for (const r of rows) {
      const key =
        r.catalogSection && grouped.has(r.catalogSection)
          ? r.catalogSection
          : OTHER_SECTION;
      grouped.get(key)!.push(r);
    }
    for (const list of grouped.values())
      list.sort((a, b) => a.catalogDisplayOrder - b.catalogDisplayOrder);
    return order
      .map((name) => ({ name, rows: grouped.get(name)! }))
      .filter((s) => s.rows.length > 0);
  }, [rows]);

  const total = rows.length;
  const done = rows.filter((r) => r.status === "Complete" || r.status === "Certified").length;
  const waived = rows.filter((r) => r.status === "Waived").length;
  // "Blocked" = DealKiller/RedFlag items still gating Mark Setup Complete.
  const blocked = rows.filter(
    (r) =>
      (r.catalogTier === "DealKiller" || r.catalogTier === "RedFlag") &&
      !DONE.has(r.status),
  ).length;
  const dkOk = gateSatisfied(rows, "DealKiller");
  const rfOk = gateSatisfied(rows, "RedFlag");
  const verificationOk = dkOk && rfOk;
  const pct = total > 0 ? Math.round(((done + waived) / total) * 100) : 0;

  const openRow = openRowId ? rows.find((r) => r.id === openRowId) ?? null : null;
  const editCatalogItem =
    editCatalogId && catalog.data
      ? catalog.data.find((c) => c.id === editCatalogId) ?? null
      : null;

  const onMarkComplete = async () => {
    try {
      await markComplete.mutateAsync();
      toast.success("Loan setup marked complete.");
    } catch (err) {
      toast.error(
        err instanceof ApiError && err.errors.length
          ? err.errors.join("; ")
          : err instanceof Error
            ? err.message
            : "Could not mark setup complete.",
      );
    }
  };

  const onGenerate = async () => {
    try {
      const created = await generate.mutateAsync();
      toast.success(`Setup checklist generated (${created.length} items).`);
    } catch (err) {
      toast.error(
        err instanceof ApiError && err.errors.length
          ? err.errors.join("; ")
          : "Could not generate the setup checklist.",
      );
    }
  };

  if (isLoading) {
    return (
      <Card>
        <p className="text-small text-gray-mid italic m-0">Loading setup checklist…</p>
      </Card>
    );
  }
  if (isError) {
    return (
      <Card>
        <p className="text-small text-status-error m-0">
          Could not load the Setup checklist. Refresh to retry.
        </p>
      </Card>
    );
  }
  if (rows.length === 0) {
    return (
      <Card>
        <h3 className="font-display font-bold text-h4 m-0">
          Checklist not yet generated
        </h3>
        <p className="text-small text-gray-mid mt-space-1 m-0">
          New loans generate their setup checklist automatically at closing
          handoff. This loan predates that wiring — generate it now (due dates
          derive from the close and first-payment dates on record).
        </p>
        {canGenerate && (
          <Button
            variant="primary"
            className="mt-space-3"
            onClick={() => void onGenerate()}
            disabled={generate.isPending}
            data-testid="setup-generate-now"
          >
            {generate.isPending ? "Generating…" : "Generate now"}
          </Button>
        )}
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-space-4" data-testid="setup-tab">
      <Card>
        <div className="flex items-start justify-between gap-space-3 flex-wrap">
          <div>
            <h3 className="font-display font-bold text-h3 m-0" data-testid="setup-progress">
              {done} of {total} complete · {blocked} blocked · {waived} waived
            </h3>
            <p className="text-small text-gray-mid mt-space-1 m-0" data-testid="setup-verification">
              {dkOk ? (
                "All Critical items complete ✓"
              ) : (
                <span className="text-status-error">
                  {rows.filter((r) => r.catalogTier === "DealKiller" && !DONE.has(r.status)).length}{" "}
                  Critical items remaining
                </span>
              )}
              {" · "}
              {rfOk ? (
                "All Important items resolved ✓"
              ) : (
                <span className="text-status-warning">
                  {rows.filter((r) => r.catalogTier === "RedFlag" && !DONE.has(r.status)).length}{" "}
                  Important items remaining
                </span>
              )}
              {loan.setupComplete && " · Setup marked complete ✓"}
            </p>
            <div className="mt-space-2 h-2 w-full max-w-md bg-white-warm rounded-pill overflow-hidden">
              <div
                className="h-full bg-gold transition-all"
                style={{ width: `${pct}%` }}
                aria-hidden
              />
            </div>
          </div>
          <div className="flex items-center gap-space-2">
            {isAdmin && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setCreateOpen(true)}
                data-testid="setup-add-catalog-item"
              >
                <Plus size={14} aria-hidden /> Add setup item
              </Button>
            )}
            {canMarkComplete && !loan.setupComplete && (
              <Button
                variant="primary"
                onClick={() => void onMarkComplete()}
                disabled={!verificationOk || markComplete.isPending}
                title={
                  !verificationOk
                    ? "Resolve all Critical and Important items before marking setup complete."
                    : undefined
                }
                data-testid="setup-mark-loan-complete"
              >
                Mark Setup Complete
              </Button>
            )}
          </div>
        </div>
      </Card>

      {sections.map((section) => (
        <section
          key={section.name}
          className="flex flex-col gap-space-2"
          data-testid={`setup-section-${section.name}`}
        >
          <h4 className="font-display font-semibold text-h4 text-onyx m-0">
            {section.name}{" "}
            <span className="text-caption text-gray-mid normal-case font-normal">
              ({section.rows.filter((r) => DONE.has(r.status)).length} of {section.rows.length})
            </span>
          </h4>
          <div className="flex flex-col gap-space-1">
            {section.rows.map((r) => (
              <SetupChecklistRow
                key={r.id}
                row={r}
                catalogCode={r.catalogCode}
                catalogTitle={r.catalogTitle}
                catalogTier={r.catalogTier}
                catalogOwnerRole={r.catalogOwnerRoleCode}
                catalogDueDateFormula={r.catalogDueDateFormula}
                onOpen={() => setOpenRowId(r.id)}
              />
            ))}
          </div>
        </section>
      ))}

      {openRow && (
        <SetupChecklistRowModal
          loanId={loan.id}
          open={!!openRow}
          onClose={() => setOpenRowId(null)}
          row={openRow}
          catalogCode={openRow.catalogCode}
          catalogTitle={openRow.catalogTitle}
          catalogTier={openRow.catalogTier}
          catalogOwnerRole={openRow.catalogOwnerRoleCode}
          catalogDescription={openRow.catalogDescription}
          catalogDueDateFormula={openRow.catalogDueDateFormula}
          onEditCatalog={
            isAdmin
              ? () => {
                  setEditCatalogId(openRow.catalogItemId);
                  setOpenRowId(null);
                }
              : undefined
          }
        />
      )}

      {isAdmin && (
        <SetupCatalogCreateModal
          loan={loan}
          open={createOpen}
          onClose={() => setCreateOpen(false)}
        />
      )}
      {editCatalogItem && (
        <SetupCatalogEditModal
          loan={loan}
          open={!!editCatalogItem}
          onClose={() => setEditCatalogId(null)}
          catalog={editCatalogItem}
        />
      )}
    </div>
  );
}
